import { useSuspenseQuery } from "@tanstack/react-query";
import Button from "components/ui/Button";
import Text from "components/ui/Text";
import { ComponentProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { availablePricingModelsQuery } from "services/pricingModel";
import { PricingModel, PurchaseStudentType } from "services/pricingModel/model";
import { trackEvent } from "services/productAnalytics";
import Dialog from "components/ui/Dialog";
import styles from "./style.module.scss";
import SubscriptionPlan from "./SubscriptionPlan";

export type ModalProps = {
  enable: boolean;
  purchaseType: PurchaseStudentType | undefined;
  selectedPricingModel?: PricingModel | null;
  onPricingModelSelected: (pricingModel: PricingModel) => void;
  title?: string;
  onClose?: () => void;
};

function ComparePlansModal(props: ModalProps) {
  const { t } = useTranslation();
  const { data: pricingModels } = useSuspenseQuery(
    availablePricingModelsQuery({ type: props.purchaseType })
  );

  const selectedModel =
    props.selectedPricingModel ?? pricingModels.find(model => model.is_selected) ?? null;

  const onPricingModelSelected = (model: PricingModel) => {
    props.onPricingModelSelected(model);
    trackEvent("pricing_model_selected", {
      planName: model.name,
      code: model.code,
      type: model.type,
    });
  };

  return (
    <Dialog
      open={props.enable}
      header={null}
      title={t("Common.comparePlans.pickYourPlan")}
      onClose={() => props.onClose?.()}
      className={styles.dialog}
    >
      <Text variant="heading-1">{t("Common.comparePlans.pickYourPlan")}</Text>

      <div className={styles.planList}>
        {pricingModels.map(model => (
          <SubscriptionPlan
            key={model.code}
            pricingModel={model}
            isSelected={selectedModel?.id === model.id}
            onSelect={() => onPricingModelSelected(model)}
          />
        ))}
      </div>
    </Dialog>
  );
}

type ComparePlansButtonProps = Omit<ModalProps, "enable" | "onClose"> &
  ComponentProps<typeof Button>;

export function ComparePlansButton(props: ComparePlansButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    title,
    selectedPricingModel,
    onPricingModelSelected,
    purchaseType,
    ...buttonProps
  } = props;

  const openModal = () => {
    setIsModalOpen(true);
    trackEvent("compare_plans_dialog_opened");
  };

  const onModelSelected = (model: PricingModel) => {
    onPricingModelSelected(model);
    setIsModalOpen(false);
  };

  return (
    <>
      <Button size="small" {...buttonProps} onClick={openModal} />
      {isModalOpen && (
        <ComparePlansModal
          title={title}
          enable={isModalOpen}
          selectedPricingModel={selectedPricingModel}
          onPricingModelSelected={onModelSelected}
          onClose={() => setIsModalOpen(false)}
          purchaseType={purchaseType}
        />
      )}
    </>
  );
}

export default ComparePlansModal;
